'use client'

/**
 * BreakevenRecoveryCalculator: free tool for "gain to recover a loss / breakeven after a loss". A loss of
 * L% needs a gain of L/(100-L)% to get back to even, which is bigger than the loss and grows fast. Shows
 * the answer for the entered loss plus a reference table of common drawdowns. Pure client math.
 */

import { useMemo, useState } from 'react'
import CalcField from '@/components/calc/CalcField'

const gainToRecover = (lossPct: number): number => (lossPct >= 100 ? Infinity : (lossPct / (100 - lossPct)) * 100)
const fmtPct = (n: number): string => (!isFinite(n) ? '∞' : `${n.toFixed(1)}%`)

const TABLE = [10, 20, 30, 40, 50, 60, 75, 90]

export default function BreakevenRecoveryCalculator() {
  const [loss, setLoss] = useState('25')

  const r = useMemo(() => {
    const l = parseFloat(loss)
    const valid = isFinite(l) && l > 0 && l < 100
    return { l, valid, gain: valid ? gainToRecover(l) : 0 }
  }, [loss])

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 sm:p-6">
      <div className="max-w-xs">
        <CalcField label="How much did you lose?" value={loss} onChange={setLoss} suffix="%" step="1" hint="The drawdown on the position, as a percent" />
      </div>

      {!r.valid ? (
        <div className="mt-5 rounded-xl border border-zinc-700/70 bg-zinc-900/60 px-4 py-6 text-center text-sm text-zinc-400">
          Enter a loss between 0% and 100% to see the gain needed to break even.
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-amber-500/30 bg-amber-500/[0.08] px-5 py-4">
          <div className="text-[12px] font-bold uppercase tracking-wider text-amber-300">Gain needed to break even</div>
          <div className="mt-1 text-[38px] font-black leading-none tabular-nums text-white">{fmtPct(r.gain)}</div>
          <div className="mt-1.5 text-[13px] text-zinc-400">
            A {r.l.toFixed(0)}% loss needs a {fmtPct(r.gain)} gain just to get back to where you started.
          </div>
        </div>
      )}

      <div className="mt-5">
        <div className="mb-2 text-[12px] font-bold uppercase tracking-wider text-zinc-400">The math of drawdowns</div>
        <div className="overflow-hidden rounded-xl border border-zinc-800">
          <div className="grid grid-cols-2 bg-zinc-900/70 px-4 py-2 text-[12px] font-bold uppercase tracking-wider text-zinc-500">
            <span>Loss</span><span className="text-right">Gain to recover</span>
          </div>
          {TABLE.map((l) => (
            <div key={l} className="grid grid-cols-2 border-t border-zinc-800/70 px-4 py-2 text-[14px] tabular-nums">
              <span className="font-semibold text-rose-300">-{l}%</span>
              <span className="text-right font-bold text-emerald-300">+{fmtPct(gainToRecover(l))}</span>
            </div>
          ))}
        </div>
      </div>

      <p className="mt-3 text-[11px] leading-relaxed text-zinc-600">
        This is why cutting losses early matters: recovery gets exponentially harder the deeper the hole. Educational tool, not investment advice.
      </p>
    </div>
  )
}
